import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import defaultEventCoverPhoto from '../../assets/Images/dafaulttourcoverphoto.png';
import styles from './ProfileModals.module.css';

// Tours Count Modal Component
const ToursModal = ({ isOpen, onClose, events, userData }) => {
  const [displayedEvents, setDisplayedEvents] = useState(6);
  const [historicalEvents, setHistoricalEvents] = useState([]);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);

  useEffect(() => {
    // Load historical event data when component mounts
    if (isOpen && userData?.userId) {
      fetchHistoricalEvents();
    }
  }, [isOpen, userData?.userId]);

  const fetchHistoricalEvents = async () => {
    if (!userData?.userId) return;
    
    setIsLoadingHistorical(true);
    try {
      const db = getFirestoreInstance();
      const userEventsSnapshot = await getDocs(collection(db, 'user_events', userData.userId, 'events'));
      
      if (!userEventsSnapshot.empty) {
        const fetchedHistoricalEvents = [];
        
        for (const eventDoc of userEventsSnapshot.docs) {
          const eventData = eventDoc.data();
          
          // Skip if not a tour
          if (eventData.eventType !== 'tour') continue;
          
          // Get event dates
          const eventDatesSnapshot = await getDocs(collection(db, 'user_events', userData.userId, 'events', eventDoc.id, 'eventDates'));
          const eventDates = eventDatesSnapshot.docs.map(dateDoc => ({
            id: dateDoc.id,
            ...dateDoc.data()
          }));
          
          fetchedHistoricalEvents.push({
            id: eventDoc.id,
            tourName: eventData.tourName,
            actName: eventData.actName,
            tourCoverPhotoUrl: eventData.coverPhotoUrl,
            eventDates,
            type: 'tour'
          });
        }
        
        setHistoricalEvents(fetchedHistoricalEvents);
      }
    } catch (error) {
      console.error('Error fetching historical tours:', error);
    } finally {
      setIsLoadingHistorical(false);
    }
  };

  const formatDateRange = (startDate, endDate) => {
    const formatDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Get only tours assigned to this specific user
  const getAssignedTours = () => {
    // First, check current tours collection
    const currentTours = getCurrentTours();
    
    // If we have current tours, return them
    if (currentTours.length > 0) return currentTours;
    
    // Otherwise, use historical tour data
    return historicalEvents.map(tour => {
      // Calculate verified dates count
      const verifiedDatesCount = tour.eventDates?.filter(date => date.verified).length || 0;
      
      return {
        ...tour,
        // Add required properties to match the structure expected by rendering code
        verifiedDatesCount,
        artistDetails: [],
        crewDetails: [],
        managerDetails: [],
        staffDetails: [],
        vendorDetails: []
      };
    });
  };
  
  const getCurrentTours = () => {
    return events.filter(event => 
      event.type === 'tour' && 
      event.assignedUsers?.some(user => user.userId === userData.userId)
    );
  };

  // Check if a tour has any verified dates across all personnel types
  const hasEventVerifiedDates = (event) => {
    // First check if this is a historical event with verified status
    if (event.eventDates?.some(date => date.verified)) {
      return true;
    }
    
    // Otherwise check current event data
    if (!event.assignedUsers) return false;
    
    // Find this user in assignedUsers
    const userAssignment = event.assignedUsers.find(user => user.userId === userData.userId);
    
    // Check if any assigned dates are verified
    return userAssignment?.assignedDates?.some(date => date.verified === true) || false;
  };

  const assignedTours = getAssignedTours();
  
  // Separate tours into verified and unverified based on user's verified dates
  const verifiedTours = assignedTours.filter(tour => hasEventVerifiedDates(tour));
  const unverifiedTours = assignedTours.filter(tour => !hasEventVerifiedDates(tour));
  
  // Count verified dates for a tour for this specific user
  const countVerifiedDates = (tour) => {
    // First check if this is a historical tour with verified status
    if (tour.verifiedDatesCount !== undefined) {
      return tour.verifiedDatesCount;
    }
    
    // Check if this is a tour with eventDates that have verified field
    if (tour.eventDates?.some(date => date.verified !== undefined)) {
      return tour.eventDates.filter(date => date.verified).length;
    }
    
    // Otherwise use the assignedUsers approach
    if (!tour.assignedUsers) return 0;
    
    // Find the user in assignedUsers
    const userAssignment = tour.assignedUsers.find(user => user.userId === userData.userId);
    
    // Count verified dates
    return userAssignment?.assignedDates?.filter(date => date.verified === true).length || 0;
  };
  
  // Sort tours based on start date in descending order
  const sortTours = (toursArray) => {
    return [...toursArray].sort((a, b) => {
      const aStartDate = a.eventDates && a.eventDates.length > 0 ? 
        new Date(a.eventDates[0]?.date) : new Date();
      const bStartDate = b.eventDates && b.eventDates.length > 0 ? 
        new Date(b.eventDates[0]?.date) : new Date();
      return bStartDate - aStartDate;
    });
  };

  const sortedVerifiedTours = sortTours(verifiedTours);
  const sortedUnverifiedTours = sortTours(unverifiedTours);

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Tours</h2>
        
        {isLoadingHistorical && (
          <div className={styles['loading-indicator']}>
            Loading tour history...
          </div>
        )}
        
        {sortedVerifiedTours.length > 0 && (
          <>
            <h3 className={styles['section-title']}>Verified Tours</h3>
            <div className={styles['tours-list']}>
              {sortedVerifiedTours.slice(0, displayedEvents).map(tour => (
                <div key={tour.id} className={styles['tour-card']}>
                  <img 
                    src={tour.tourCoverPhotoUrl || defaultEventCoverPhoto} 
                    alt={tour.tourName}
                    className={styles['tour-cover-photo']}
                  />
                  <div className={styles['tour-card-content']}>
                    <h3>{tour.tourName}</h3>
                    <p className={styles['act-name']}>{tour.actName}</p>
                    <p className={styles['tour-dates']}>
                      {formatDateRange(
                        tour.eventDates && tour.eventDates.length > 0 ? tour.eventDates[0]?.date : null, 
                        tour.eventDates && tour.eventDates.length > 0 ? tour.eventDates[tour.eventDates.length - 1]?.date : null
                      )}
                    </p>
                    <p className={styles['shows-count']}>
                      {countVerifiedDates(tour)} verified {countVerifiedDates(tour) === 1 ? 'show' : 'shows'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {sortedUnverifiedTours.length > 0 && (
          <>
            <h3 className={styles['section-title']}>Pending Tours</h3>
            <div className={`${styles['tours-list']} ${styles['unverified-tours-list']}`}>
              {sortedUnverifiedTours.slice(0, displayedEvents).map(tour => (
                <div key={tour.id} className={styles['tour-card']}>
                  <img 
                    src={tour.tourCoverPhotoUrl || defaultEventCoverPhoto} 
                    alt={tour.tourName}
                    className={styles['tour-cover-photo']}
                  />
                  <div className={styles['tour-card-content']}>
                    <h3>{tour.tourName}</h3>
                    <p className={styles['act-name']}>{tour.actName}</p>
                    <p className={styles['tour-dates']}>
                      {formatDateRange(
                        tour.eventDates && tour.eventDates.length > 0 ? tour.eventDates[0]?.date : null,
                        tour.eventDates && tour.eventDates.length > 0 ? tour.eventDates[tour.eventDates.length - 1]?.date : null
                      )}
                    </p>
                    <div className={styles['unverified-message']}>
                      This tour will be included when a show is verified
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {assignedTours.length === 0 && !isLoadingHistorical && (
          <div className={styles['no-data-message']}>
            No tours available
          </div>
        )}
        
        {displayedEvents < (sortedVerifiedTours.length + sortedUnverifiedTours.length) && (
          <button 
            className={styles['load-more-button']}
            onClick={() => setDisplayedEvents(prev => prev + 6)}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};


// Shows Count Modal Component
const ShowsModal = ({ isOpen, onClose, events, userData }) => {
  const [displayedShows, setDisplayedShows] = useState(15);
  const [historicalShows, setHistoricalShows] = useState([]);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);

  useEffect(() => {
    // Load historical show data when component mounts
    if (isOpen && userData?.userId) {
      fetchHistoricalShows();
    }
  }, [isOpen, userData?.userId]);

  const fetchHistoricalShows = async () => {
    if (!userData?.userId) return;
    
    setIsLoadingHistorical(true);
    try {
      const db = getFirestoreInstance();
      
      // First fetch all events
      const userEventsSnapshot = await getDocs(collection(db, 'user_events', userData.userId, 'events'));
      
      if (!userEventsSnapshot.empty) {
        let allHistoricalShows = [];
        
        // Process each event (tour or show)
        for (const eventDoc of userEventsSnapshot.docs) {
          const eventData = eventDoc.data();
          
          // Get verified dates for this event
          const eventDatesQuery = query(
            collection(db, 'user_events', userData.userId, 'events', eventDoc.id, 'eventDates'),
            where('verified', '==', true)
          );
          
          const eventDatesSnapshot = await getDocs(eventDatesQuery);
          
          if (!eventDatesSnapshot.empty) {
            const verifiedDates = eventDatesSnapshot.docs.map(dateDoc => {
              const dateData = dateDoc.data();
              return {
                ...dateData,
                id: dateDoc.id,
                eventId: eventDoc.id,
                tourName: eventData.tourName,
                showName: eventData.eventType === 'show' ? eventData.showName : null,
                actName: eventData.actName,
                eventType: eventData.eventType
              };
            });
            
            allHistoricalShows = [...allHistoricalShows, ...verifiedDates];
          }
        }
        
        setHistoricalShows(allHistoricalShows);
      }
    } catch (error) {
      console.error('Error fetching historical shows:', error);
    } finally {
      setIsLoadingHistorical(false);
    }
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = parseDate(dateString);
    if (!date || isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  // Get verified shows for this specific user
  const getVerifiedShows = () => {
    // First try to get from current events
    const currentShows = getCurrentVerifiedShows();
    
    // If we have current shows, return them
    if (currentShows.length > 0) return currentShows;
    
    // Otherwise, use historical show data
    return historicalShows;
  };
  
  const getCurrentVerifiedShows = () => {
    let allVerifiedShows = [];
    
    events.forEach(event => {
      // Use a map to avoid duplicates
      const verifiedDateMap = new Map();
      
      // Find the user in assignedUsers
      const userAssignment = event.assignedUsers?.find(user => user.userId === userData.userId);
      
      // If user is assigned to this event
      if (userAssignment) {
        // Check each assigned date
        userAssignment.assignedDates?.forEach(assignedDate => {
          if (assignedDate.verified === true) {
            // Find the actual date data
            const eventDate = event.dates?.find(td => td.id === assignedDate.dateDocId);
            
            if (eventDate && !eventDate.cancelled) {
              verifiedDateMap.set(assignedDate.dateDocId, {
                ...eventDate,
                tourName: event.tourName,
                showName: event.type === 'show' ? event.showName || event.tourName : null,
                actName: event.actName,
                eventId: event.id,
                eventType: event.type,
                assignedDate: assignedDate
              });
            }
          }
        });
      }
      
      // Add all verified shows from this event to our overall array
      allVerifiedShows = [...allVerifiedShows, ...verifiedDateMap.values()];
    });
    
    return allVerifiedShows;
  };

  const verifiedShows = getVerifiedShows();
  
  // Sort shows by date in descending order
  const sortedShows = verifiedShows.sort((a, b) => {
    const dateA = a.rescheduleDate?.isRescheduled ? parseDate(a.rescheduleDate.newDate) : parseDate(a.date);
    const dateB = b.rescheduleDate?.isRescheduled ? parseDate(b.rescheduleDate.newDate) : parseDate(b.date);
    return dateB - dateA;
  });

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Verified Shows</h2>
        
        {isLoadingHistorical && (
          <div className={styles['loading-indicator']}>
            Loading show history...
          </div>
        )}
        
        {sortedShows.length > 0 ? (
          <div className={styles['shows-list']}>
            {sortedShows.slice(0, displayedShows).map((show, index) => {
              // Get the correct date to display based on reschedule status
              const showDate = show.rescheduleDate?.isRescheduled 
                ? formatDate(show.rescheduleDate.newDate)
                : formatDate(show.date);

              return (
                <div key={`${show.eventId}-${show.id}-${index}`} className={styles['show-card']}>
                  <div className={styles['show-date']}>{showDate}</div>
                  <div className={styles['show-details']}>
                    <h3>{show.eventType === 'show' ? show.showName || show.tourName : show.tourName}</h3>
                    <p className={styles['act-name']}>{show.actName}</p>
                    <p className={styles['venue-name']}>
                      {show.event?.venueDetails?.venueData?.displayName || 
                       show.venueDetails?.displayName || 
                       'Venue not specified'}
                    </p>
                    <p className={styles['venue-address']}>
                      {show.event?.venueDetails?.venueData?.formattedAddress || 
                       show.venueDetails?.formattedAddress || 
                       'Address not specified'}
                    </p>
                    {show.rescheduleDate?.isRescheduled && (
                      <p className={styles['rescheduled-note']}>
                        Rescheduled from {formatDate(show.date)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles['no-data-message']}>
            No verified shows available
          </div>
        )}
        
        {displayedShows < sortedShows.length && (
          <button 
            className={styles['load-more-button']}
            onClick={() => setDisplayedShows(prev => prev + 15)}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};


// Users Count Modal Component
const UsersModal = ({ isOpen, onClose, events, currentUser, onUserSelect }) => {
  const [displayedUsers, setDisplayedUsers] = useState(15);
  
  const getUniqueUsers = () => {
    const uniqueUsers = new Map();
    
    events.forEach(event => {
      // Process assignedUsers
      if (event.assignedUsers && Array.isArray(event.assignedUsers)) {
        event.assignedUsers.forEach(user => {
          if (user && user.userId) {
            const existingUser = uniqueUsers.get(user.userId) || { 
              ...user, 
              events: [] 
            };
            existingUser.events = [...(existingUser.events || []), event.id];
            uniqueUsers.set(user.userId, existingUser);
          }
        });
      }
    });
    
    // Convert Map to Array and filter out empty or invalid entries
    return Array.from(uniqueUsers.values()).filter(user => 
      user && user.userId && user.userName
    );
  };

  const users = getUniqueUsers();

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Users</h2>
        {users.length > 0 ? (
          <div className={styles['users-list']}>
            {users.slice(0, displayedUsers).map(user => (
              <div 
                key={user.userId} 
                className={styles['user-card']}
                onClick={() => {
                  onUserSelect(user);
                  onClose();
                }}
              >
                <img
                  src={user.profilePictureUrl || defaultProfilePhoto}
                  alt={user.fullName || user.userName}
                  className={styles['user-photo']}
                />
                <div className={styles['user-info']}>
                  <div className={styles['user-name-container']}>
                    <h3>{user.fullName || user.userName}</h3>
                    {user.idVerificationStatus === 'Verified' && (
                      <img
                        src="/verified-badge.png"
                        alt="Verified"
                        className={styles['verified-badge']}
                      />
                    )}
                  </div>
                  <p className={styles.username}>@{user.userName}</p>
                  <p className={styles['user-type']}>{user.userType}</p>
                  <p className={styles['user-subtype']}>{user.userSubType}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles['no-data-message']}>
            No users available
          </div>
        )}
        
        {displayedUsers < users.length && (
          <button 
            className={styles['load-more-button']}
            onClick={() => setDisplayedUsers(prev => prev + 15)}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};

// PropTypes definitions
ToursModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    tourName: PropTypes.string,
    showName: PropTypes.string,
    actName: PropTypes.string,
    tourCoverPhotoUrl: PropTypes.string,
    dates: PropTypes.array,
    assignedUsers: PropTypes.array
  })).isRequired,
  userData: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired
  }).isRequired
};

ShowsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    tourName: PropTypes.string,
    showName: PropTypes.string,
    actName: PropTypes.string,
    dates: PropTypes.array,
    assignedUsers: PropTypes.array
  })).isRequired,
  userData: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired
  }).isRequired
};

UsersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    assignedUsers: PropTypes.array
  })).isRequired,
  currentUser: PropTypes.shape({
    userId: PropTypes.string.isRequired
  }).isRequired,
  onUserSelect: PropTypes.func.isRequired
};

export { ToursModal, ShowsModal, UsersModal };